import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cliente } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type { OperadorFioriAdmin, RespuestaApi } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, Power, Truck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * `/admin/fiori-operadores` — Alta y gestión de los operadores Fiori (rol
 * OperadorFiori). A diferencia del PIN de Laboratorio (uno solo,
 * compartido), acá cada chofer tiene su propio nombre + PIN — necesario
 * para que cada uno vea solo las coladas que él mismo registró.
 *
 * El operador entra a `/fiori-login`, elige su nombre de una lista y
 * teclea su PIN.
 */

function formatearFecha(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminFioriOperadoresPantalla() {
  const qc = useQueryClient();
  const invalidar = () => qc.invalidateQueries({ queryKey: ['admin', 'fiori-operadores'] });

  const consulta = useQuery<RespuestaApi<{ operadores: OperadorFioriAdmin[] }>>({
    queryKey: ['admin', 'fiori-operadores'],
    queryFn: () => cliente.fioriOperadores.listar(),
  });
  const operadores = consulta.data?.data?.operadores ?? [];

  const [nombreNuevo, setNombreNuevo] = useState('');
  const [pinNuevo, setPinNuevo] = useState('');
  const [pinNuevoConfirm, setPinNuevoConfirm] = useState('');

  const crear = useMutation<
    RespuestaApi<OperadorFioriAdmin>,
    Error,
    { nombre: string; pin: string }
  >({
    mutationFn: (body) => cliente.fioriOperadores.crear(body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success(`Operador "${r.data.nombre}" creado`);
        setNombreNuevo('');
        setPinNuevo('');
        setPinNuevoConfirm('');
        invalidar();
      } else {
        toast.error(r.error?.mensaje ?? 'Error al crear el operador');
      }
    },
    onError: (e) => toast.error('No se pudo crear el operador', { description: e.message }),
  });

  const pinValido = /^\d{4}$/.test(pinNuevo);
  const confirmValido = pinNuevo === pinNuevoConfirm && pinValido;
  const puedeCrear = nombreNuevo.trim() !== '' && confirmValido && !crear.isPending;

  const onCrear = () => {
    if (!puedeCrear) return;
    crear.mutate({ nombre: nombreNuevo.trim(), pin: pinNuevo });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Truck className="h-6 w-6" /> Operadores Fiori
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Choferes/operadores de las autocargables — no tienen cuenta Microsoft, entran a{' '}
          <code className="rounded bg-muted px-1 text-xs">/fiori-login</code> eligiendo su nombre y
          tecleando su propio PIN. Cada uno ve solo las coladas que él mismo registró.
        </p>
      </div>

      {/* Alta de operador nuevo */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Dar de alta un operador</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="nombre-nuevo">Nombre</Label>
            <Input
              id="nombre-nuevo"
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
              maxLength={100}
              placeholder="Ej: Carlos Vargas"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="pin-nuevo">
                PIN (4 dígitos) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pin-nuevo"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={pinNuevo}
                onChange={(e) => setPinNuevo(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                className="text-center font-mono text-2xl tracking-widest"
                autoComplete="new-password"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pin-nuevo-confirm">Confirmar PIN</Label>
              <Input
                id="pin-nuevo-confirm"
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={pinNuevoConfirm}
                onChange={(e) => setPinNuevoConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                className={cn(
                  'text-center font-mono text-2xl tracking-widest',
                  pinNuevoConfirm.length === 4 &&
                    !confirmValido &&
                    'border-destructive focus-visible:ring-destructive',
                )}
                autoComplete="new-password"
              />
              {pinNuevoConfirm.length === 4 && !confirmValido && (
                <p className="text-xs text-destructive">Los PINs no coinciden.</p>
              )}
            </div>
          </div>

          <Button onClick={onCrear} disabled={!puedeCrear} className="gap-1.5">
            <Plus className="h-4 w-4" />
            {crear.isPending ? 'Creando…' : 'Crear operador'}
          </Button>
        </CardContent>
      </Card>

      {/* Listado */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Operadores dados de alta</CardTitle>
        </CardHeader>
        <CardContent>
          {consulta.isLoading && <Skeleton className="h-24 w-full" />}
          {consulta.error && (
            <p className="text-sm text-destructive">
              Error al cargar operadores: {consulta.error.message}
            </p>
          )}
          {!consulta.isLoading && operadores.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Todavía no hay operadores dados de alta.
            </p>
          )}
          <div className="space-y-2">
            {operadores.map((o) => (
              <FilaOperador key={o.id} operador={o} onCambio={invalidar} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FilaOperador({
  operador,
  onCambio,
}: {
  operador: OperadorFioriAdmin;
  onCambio: () => void;
}) {
  const [rotando, setRotando] = useState(false);
  const [pinRotar, setPinRotar] = useState('');

  const rotarPin = useMutation<RespuestaApi<OperadorFioriAdmin>, Error, string>({
    mutationFn: (pin) => cliente.fioriOperadores.rotarPin(operador.id, { pin }),
    onSuccess: (r) => {
      if (r.data) {
        toast.success(`PIN de "${operador.nombre}" actualizado`);
        setRotando(false);
        setPinRotar('');
        onCambio();
      } else {
        toast.error(r.error?.mensaje ?? 'Error al rotar el PIN');
      }
    },
    onError: (e) => toast.error('No se pudo rotar el PIN', { description: e.message }),
  });

  const cambiarActivo = useMutation<RespuestaApi<OperadorFioriAdmin>, Error, boolean>({
    mutationFn: (activo) => cliente.fioriOperadores.cambiarActivo(operador.id, { activo }),
    onSuccess: (r) => {
      if (r.data) {
        toast.success(
          r.data.activo ? `"${operador.nombre}" activado` : `"${operador.nombre}" desactivado`,
        );
        onCambio();
      } else {
        toast.error(r.error?.mensaje ?? 'Error al cambiar el estado');
      }
    },
    onError: (e) => toast.error('No se pudo cambiar el estado', { description: e.message }),
  });

  const pinValido = /^\d{4}$/.test(pinRotar);

  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-medium">
            {operador.nombre}
            {operador.activo ? (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-900">
                Activo
              </span>
            ) : (
              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600">
                Inactivo
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Último uso: {formatearFecha(operador.ultimo_uso_en)}
            {operador.bloqueado_hasta && (
              <span className="ml-2 text-destructive">
                Bloqueado hasta {formatearFecha(operador.bloqueado_hasta)}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setRotando((v) => !v)}
          >
            <KeyRound className="h-3.5 w-3.5" /> Rotar PIN
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={cambiarActivo.isPending}
            onClick={() => cambiarActivo.mutate(!operador.activo)}
          >
            <Power className="h-3.5 w-3.5" /> {operador.activo ? 'Desactivar' : 'Activar'}
          </Button>
        </div>
      </div>

      {rotando && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
          <Input
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={pinRotar}
            onChange={(e) => setPinRotar(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="Nuevo PIN"
            className="w-32 text-center font-mono text-lg tracking-widest"
            autoComplete="new-password"
          />
          <Button
            size="sm"
            disabled={!pinValido || rotarPin.isPending}
            onClick={() => rotarPin.mutate(pinRotar)}
          >
            {rotarPin.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      )}
    </div>
  );
}
